// Story 1.32 — admin financial intervention tools at /admin/financial-tools.
// Wrapped by <AdminGuard> in App.tsx.
//
// Four solo-founder safety-valve tools on one page:
//   1. Manual refund (bypasses 14-day window)
//   2. Ledger adjustment (credit / debit)
//   3. Trigger immediate reconciliation (stub pending Epic 6 Story 6.7)
//   4. Refund stuck escrow (501 stub pending Epic 2/5)
//
// Story 8.3 removed a fifth, "Cancel stuck payout". Guardrail G4: payout
// overrides and the user-payout cancellation surface are REMOVED, not gated.
// With no user payout rail there is no stuck payout to rescue, and an admin
// form that can move wallet-pot value toward an external destination is the
// single thing G4 exists to forbid. The audit LABEL for the historical
// discriminant stays in `features/admin/intervention-kinds.ts` — real rows
// carry it and would otherwise render unlabelled.
//
// Every action requires a min-30-char justification AND emits an
// AdminFinancialIntervention audit row AND emails both the affected
// user AND ADMIN_NOTIFICATION_EMAIL (Mads-self-supervision).

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  DEFAULT_REFUNDABLE_TOPUPS_FILTERS,
  type RefundableTopUpItem,
  useAdminRefundableTopUps,
} from "../features/admin/useAdminRefundableTopUps";
import {
  ManualRefundError,
  useExecuteManualRefund,
} from "../features/admin/useExecuteManualRefund";
import {
  LedgerAdjustmentError,
  useExecuteLedgerAdjustment,
  type LedgerAdjustmentDirection,
} from "../features/admin/useExecuteLedgerAdjustment";
import { useExecuteTriggerReconciliation } from "../features/admin/useExecuteTriggerReconciliation";

// ----- Constants -----

const JUSTIFICATION_MIN = 30;
const JUSTIFICATION_MAX = 2000;
const LEDGER_ADJ_MAX_CENTS = 1_000_000; // €10,000

// UUID v4 regex (case-insensitive).
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(s: string): boolean {
  return UUID_RE.test(s.trim());
}

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const frac = (abs % 100).toString().padStart(2, "0");
  return `${sign}€${whole}.${frac}`;
}

function freshIdempotencyKey(): string {
  // crypto.randomUUID is available in all modern browsers Greenhold
  // supports (per package.json browserslist).
  return crypto.randomUUID();
}

// ----- Page -----

export function AdminFinancialToolsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUser = searchParams.get("user") ?? "";
  const [userIdInput, setUserIdInput] = useState(initialUser);
  const [activeUserId, setActiveUserId] = useState(initialUser);

  function loadUser() {
    const trimmed = userIdInput.trim();
    if (!isUuid(trimmed)) {
      toast.error("Enter a valid UUID");
      return;
    }
    setActiveUserId(trimmed);
    const next = new URLSearchParams(searchParams);
    next.set("user", trimmed);
    setSearchParams(next, { replace: true });
  }

  function clearUser() {
    setUserIdInput("");
    setActiveUserId("");
    const next = new URLSearchParams(searchParams);
    next.delete("user");
    setSearchParams(next, { replace: true });
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-8">
      <div className="max-w-4xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header>
          <h1 className="text-3xl font-heading font-bold mb-2">
            Financial tools
          </h1>
          <p className="text-warning border-l-4 border-warning pl-3 py-1 bg-warning/5">
            ⚠ These tools bypass normal flow controls. Proceed only after
            verifying the situation. Every action is permanently audit-logged
            and emails both the affected user and your admin-log inbox.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>User lookup</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                placeholder="user UUID (e.g. 9f4c2a8e-1234-...)"
                className="flex-1 px-3 py-2 border rounded font-mono text-sm"
                aria-label="user UUID lookup"
              />
              <Button variant="primary" onClick={loadUser}>
                Load
              </Button>
              {activeUserId && (
                <Button variant="tertiary" onClick={clearUser}>
                  Clear
                </Button>
              )}
            </div>
            {activeUserId && (
              <p className="mt-2 text-sm text-brand-forest-700 font-mono">
                Loaded: {activeUserId}
              </p>
            )}
          </CardContent>
        </Card>

        {activeUserId && (
          <>
            <ManualRefundSection userId={activeUserId} />
            <LedgerAdjustmentSection userId={activeUserId} />
          </>
        )}

        <TriggerReconciliationSection />
        <RefundEscrowStubSection />
      </div>
    </main>
  );
}

// ============================================================================
// Section 1 — Manual refund
// ============================================================================

function ManualRefundSection({ userId }: { userId: string }) {
  const { data, isLoading, isError } = useAdminRefundableTopUps(
    userId,
    DEFAULT_REFUNDABLE_TOPUPS_FILTERS,
  );
  const [dialogFor, setDialogFor] = useState<RefundableTopUpItem | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manual refund</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && <p>Loading top-ups…</p>}
        {isError && <p className="text-destructive">Failed to load top-ups.</p>}
        {data && data.data.length === 0 && (
          <EmptyState
            aria-label="No top-ups"
            description="No top-ups for this user."
          />
        )}
        {data && data.data.length > 0 && (
          <ul className="divide-y">
            {data.data.map((item) => (
              <li key={item.ledger_entry_id} className="py-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="text-sm">
                    <div>
                      <strong>{formatEur(item.original_amount_cents)}</strong>{" "}
                      on {new Date(item.created_at).toLocaleDateString()}{" "}
                      <span className="text-brand-forest-600">
                        ({item.days_since_topup}d ago)
                      </span>
                    </div>
                    <div className="text-brand-forest-600">
                      already refunded: {formatEur(item.already_refunded_cents)} ·
                      remaining: {formatEur(item.remaining_after_refunds_cents)} ·
                      card ••{item.card_last4 ?? "••"}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => setDialogFor(item)}
                    aria-label={`Manually refund top-up of ${formatEur(
                      item.original_amount_cents,
                    )}`}
                  >
                    Refund
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {dialogFor && (
        <ManualRefundDialog
          userId={userId}
          item={dialogFor}
          onClose={() => setDialogFor(null)}
        />
      )}
    </Card>
  );
}

function ManualRefundDialog({
  userId,
  item,
  onClose,
}: {
  userId: string;
  item: RefundableTopUpItem;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState(
    String(item.remaining_after_refunds_cents),
  );
  const [justification, setJustification] = useState("");
  const mutation = useExecuteManualRefund();

  const amountCents = Number.parseInt(amount, 10);
  const amountValid =
    Number.isFinite(amountCents) &&
    amountCents > 0 &&
    amountCents <= item.original_amount_cents;
  const justificationValid =
    justification.trim().length >= JUSTIFICATION_MIN &&
    justification.trim().length <= JUSTIFICATION_MAX;
  const canConfirm = amountValid && justificationValid && !mutation.isPending;

  async function confirm() {
    try {
      const result = await mutation.mutateAsync({
        user_id: userId,
        source_ledger_entry_id: item.ledger_entry_id,
        amount_cents: amountCents,
        justification: justification.trim(),
        idempotency_key: freshIdempotencyKey(),
      });
      toast.success(
        `Refunded ${formatEur(result.data.amount_cents)}. New balance: ${formatEur(result.data.new_balance_cents)}.`,
      );
      onClose();
    } catch (err) {
      if (err instanceof ManualRefundError) {
        toast.error(err.message);
      } else {
        toast.error(String(err));
      }
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Manually refund top-up?</AlertDialogTitle>
          <AlertDialogDescription>
            Original: {formatEur(item.original_amount_cents)} on card ••
            {item.card_last4 ?? "••"} (top-up {item.days_since_topup}d ago).
            Bypasses 14-day PSD2 window. This action is irreversible.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              Refund amount (cents)
            </label>
            <input
              type="number"
              min={1}
              max={item.original_amount_cents}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded font-mono"
            />
            {Number.isFinite(amountCents) && (
              <p className="text-xs text-brand-forest-600 mt-1">
                = {formatEur(amountCents)}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Justification ({JUSTIFICATION_MIN}-{JUSTIFICATION_MAX} chars)
            </label>
            <textarea
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border rounded text-sm"
              aria-label="Refund justification"
            />
            <p
              className={`text-xs mt-1 ${
                justificationValid ? "text-brand-forest-600" : "text-destructive"
              }`}
            >
              {justification.trim().length} / {JUSTIFICATION_MAX}
            </p>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={confirm}
            disabled={!canConfirm}
            data-disabled={!canConfirm}
          >
            {mutation.isPending ? "Refunding…" : "Refund"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ============================================================================
// Section 2 — Ledger adjustment
// ============================================================================

function LedgerAdjustmentSection({ userId }: { userId: string }) {
  const [direction, setDirection] = useState<LedgerAdjustmentDirection>("credit");
  const [amount, setAmount] = useState("");
  const [justification, setJustification] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useExecuteLedgerAdjustment();

  const amountCents = Number.parseInt(amount, 10);
  const amountValid =
    Number.isFinite(amountCents) &&
    amountCents > 0 &&
    amountCents <= LEDGER_ADJ_MAX_CENTS;
  const justificationValid =
    justification.trim().length >= JUSTIFICATION_MIN &&
    justification.trim().length <= JUSTIFICATION_MAX;
  const canSubmit = amountValid && justificationValid;

  async function confirm() {
    try {
      const result = await mutation.mutateAsync({
        user_id: userId,
        direction,
        amount_cents: amountCents,
        justification: justification.trim(),
        idempotency_key: freshIdempotencyKey(),
      });
      toast.success(
        `${direction === "credit" ? "Credited" : "Debited"} ${formatEur(
          result.data.amount_cents,
        )}. New balance: ${formatEur(result.data.new_balance_cents)}.`,
      );
      setAmount("");
      setJustification("");
      setConfirmOpen(false);
    } catch (err) {
      if (err instanceof LedgerAdjustmentError) {
        toast.error(err.message);
      } else {
        toast.error(String(err));
      }
      setConfirmOpen(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ledger adjustment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <fieldset>
          <legend className="block text-sm font-medium mb-1">Direction</legend>
          <label className="inline-flex items-center mr-4">
            <input
              type="radio"
              name="direction"
              value="credit"
              checked={direction === "credit"}
              onChange={() => setDirection("credit")}
              className="mr-1"
            />
            Credit (to user)
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="direction"
              value="debit"
              checked={direction === "debit"}
              onChange={() => setDirection("debit")}
              className="mr-1"
            />
            Debit (from user)
          </label>
        </fieldset>
        <div>
          <label className="block text-sm font-medium mb-1">
            Amount (cents, max €10,000)
          </label>
          <input
            type="number"
            min={1}
            max={LEDGER_ADJ_MAX_CENTS}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border rounded font-mono"
          />
          {Number.isFinite(amountCents) && amountCents > 0 && (
            <p className="text-xs text-brand-forest-600 mt-1">
              = {formatEur(amountCents)}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Justification ({JUSTIFICATION_MIN}-{JUSTIFICATION_MAX} chars)
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded text-sm"
            aria-label="Adjustment justification"
          />
          <p
            className={`text-xs mt-1 ${
              justificationValid ? "text-brand-forest-600" : "text-destructive"
            }`}
          >
            {justification.trim().length} / {JUSTIFICATION_MAX}
          </p>
        </div>
        <Button
          variant={direction === "credit" ? "primary" : "destructive"}
          disabled={!canSubmit}
          onClick={() => setConfirmOpen(true)}
        >
          Apply adjustment
        </Button>
      </CardContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {direction === "credit" ? "Credit" : "Debit"} {formatEur(amountCents)}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {direction === "credit" ? "Credit" : "Debit"} {formatEur(amountCents)}{" "}
              {direction === "credit" ? "to" : "from"} user{" "}
              <code>{userId.slice(0, 8)}</code>. Justification:{" "}
              "{justification.trim().slice(0, 80)}
              {justification.trim().length > 80 ? "…" : ""}". This action is
              irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={mutation.isPending}>
              {mutation.isPending ? "Applying…" : "Apply"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ============================================================================
// (Section 3 — "Cancel stuck payout" — DELETED by Story 8.3, guardrail G4.
// The sections below keep their original numbering so the page and its
// runbooks still line up.)
// ============================================================================

// ============================================================================
// Section 4 — Trigger immediate reconciliation
// ============================================================================

function TriggerReconciliationSection() {
  const [justification, setJustification] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const mutation = useExecuteTriggerReconciliation();

  const justificationValid =
    justification.trim().length >= JUSTIFICATION_MIN &&
    justification.trim().length <= JUSTIFICATION_MAX;

  async function confirm() {
    try {
      const result = await mutation.mutateAsync({
        justification: justification.trim(),
        idempotency_key: freshIdempotencyKey(),
      });
      toast.success(
        `Reconciliation queued (deferred to Story 6.7). Recorded ${new Date(
          result.data.recorded_at,
        ).toLocaleString()}.`,
      );
      setJustification("");
      setConfirmOpen(false);
    } catch (err) {
      toast.error(String(err));
      setConfirmOpen(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Trigger immediate reconciliation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-brand-forest-700">
          Records a checkpoint in <code>system_config</code> that Epic 6 Story 6.7's
          reconciliation worker will pick up on next boot. No worker runs at v1
          ship.
        </p>
        <div>
          <label className="block text-sm font-medium mb-1">
            Justification ({JUSTIFICATION_MIN}-{JUSTIFICATION_MAX} chars)
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded text-sm"
            aria-label="Reconciliation justification"
          />
          <p
            className={`text-xs mt-1 ${
              justificationValid ? "text-brand-forest-600" : "text-destructive"
            }`}
          >
            {justification.trim().length} / {JUSTIFICATION_MAX}
          </p>
        </div>
        <Button
          variant="primary"
          disabled={!justificationValid}
          onClick={() => setConfirmOpen(true)}
        >
          Trigger reconciliation now
        </Button>
      </CardContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Trigger reconciliation immediately?</AlertDialogTitle>
            <AlertDialogDescription>
              This records a checkpoint that the worker (Epic 6 Story 6.7) will
              pick up on next run. Justification will be permanent-logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirm} disabled={mutation.isPending}>
              {mutation.isPending ? "Triggering…" : "Trigger"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

// ============================================================================
// Section 5 — Refund stuck escrow (501 stub)
// ============================================================================

function RefundEscrowStubSection() {
  const [escrowId, setEscrowId] = useState("");
  const [justification, setJustification] = useState("");
  const [pending, setPending] = useState(false);

  async function tryStub() {
    setPending(true);
    try {
      const response = await fetch(
        "/api/v1/admin/financial-tools/refund-escrow",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": "application/json",
            "x-idempotency-key": freshIdempotencyKey(),
          },
          body: JSON.stringify({
            escrow_holding_id: escrowId.trim(),
            justification: justification.trim(),
          }),
        },
      );
      const body = await response.json().catch(() => ({}));
      if (response.status === 501 && body?.error?.note) {
        toast(body.error.note as string, { icon: "ℹ" });
      } else if (!response.ok) {
        toast.error(
          (body?.error?.message as string) ?? `HTTP ${response.status}`,
        );
      } else {
        toast.success("Escrow refund accepted.");
      }
    } catch (err) {
      toast.error(String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Refund stuck escrow</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-brand-forest-700">
          Escrow refunds are pending Epic 2 / Epic 5. This tool wires up
          automatically when those tables land. The "Try" button below
          exercises the 501 stub so you can verify the surface.
        </p>
        <div>
          <label className="block text-sm font-medium mb-1">
            Escrow holding id
          </label>
          <input
            type="text"
            value={escrowId}
            onChange={(e) => setEscrowId(e.target.value)}
            placeholder="(future) escrow_holding UUID"
            className="w-full px-3 py-2 border rounded font-mono text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Justification ({JUSTIFICATION_MIN}-{JUSTIFICATION_MAX} chars)
          </label>
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded text-sm"
          />
        </div>
        <Button
          variant="tertiary"
          disabled={pending || !isUuid(escrowId) || justification.trim().length < JUSTIFICATION_MIN}
          onClick={tryStub}
        >
          {pending ? "Trying…" : "Try"}
        </Button>
      </CardContent>
    </Card>
  );
}
